import { lazy, Suspense, type ComponentType } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/app-layout";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { RouteLoading } from "@/components/layout/route-loading";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyPage(factory: () => Promise<Record<string, any>>, name: string) {
  const LazyComponent = lazy(() =>
    factory().then((mod) => ({ default: mod[name] as ComponentType })),
  );
  return (
    <Suspense fallback={<RouteLoading />}>
      <LazyComponent />
    </Suspense>
  );
}

// ── Public ──
const LandingPage = () => lazyPage(() => import("@/features/marketing/pages/landing.page"), "LandingPage");
const LoginPage = () => lazyPage(() => import("@/features/auth/pages/login.page"), "LoginPage");
const NotFoundPage = () => lazyPage(() => import("@/pages/not-found.page"), "NotFoundPage");
const ResellerStorefront = () => lazyPage(() => import("@/features/reseller/pages/rs-storefront.page"), "ResellerStorefrontPage");

// ── Wholesaler ──
const WhDashboard = () => lazyPage(() => import("@/features/wholesaler/pages/wh-dashboard.page"), "WhDashboardPage");
const WhProductForm = () => lazyPage(() => import("@/features/wholesaler/pages/wh-product-form.page"), "WhProductFormPage");
const WhOrders = () => lazyPage(() => import("@/features/wholesaler/pages/wh-orders.page"), "WhOrdersPage");
const WhAnalytics = () => lazyPage(() => import("@/features/wholesaler/pages/wh-analytics.page"), "WhAnalyticsPage");
const WhProfile = () => lazyPage(() => import("@/features/wholesaler/pages/wh-profile.page"), "WhProfilePage");
const WhConnections = () => lazyPage(() => import("@/features/wholesaler/pages/wh-connections.page"), "WhConnectionsPage");

// ── Reseller ──
const RsFeed = () => lazyPage(() => import("@/features/reseller/pages/rs-feed.page"), "RsFeedPage");
const RsWholesalers = () => lazyPage(() => import("@/features/reseller/pages/rs-wholesalers.page"), "RsWholesalersPage");
const RsOrders = () => lazyPage(() => import("@/features/reseller/pages/rs-orders.page"), "RsOrdersPage");
const RsIntegration = () => lazyPage(() => import("@/features/reseller/pages/rs-integration.page"), "RsIntegrationPage");
const RsProfile = () => lazyPage(() => import("@/features/reseller/pages/rs-profile.page"), "RsProfilePage");

// ── Admin ──
const AdOverview = () => lazyPage(() => import("@/features/admin/pages/ad-overview.page"), "AdOverviewPage");
const AdMembers = () => lazyPage(() => import("@/features/admin/pages/ad-members.page"), "AdMembersPage");
const AdOrders = () => lazyPage(() => import("@/features/admin/pages/ad-orders.page"), "AdOrdersPage");
const AdMarket = () => lazyPage(() => import("@/features/admin/pages/ad-market.page"), "AdMarketPage");
const AdAudit = () => lazyPage(() => import("@/features/admin/pages/ad-audit.page"), "AdAuditPage");
const AdChangelog = () => lazyPage(() => import("@/features/admin/pages/ad-changelog.page"), "AdChangelogPage");
const AdReleaseNotes = () => lazyPage(() => import("@/features/admin/pages/ad-release-notes.page"), "AdReleaseNotesPage");
const AdSettings = () => lazyPage(() => import("@/features/admin/pages/ad-settings.page"), "AdSettingsPage");

export const router = createBrowserRouter([
  { path: "/", element: LandingPage() },
  { path: "/login", element: LoginPage() },
  { path: "/store", element: ResellerStorefront() },
  { path: "/store/:apiKey", element: ResellerStorefront() },

  {
    path: "/wholesaler",
    element: (
      <ProtectedRoute role="wholesaler">
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: WhDashboard() },
      { path: "products/new", element: WhProductForm() },
      { path: "products/:id", element: WhProductForm() },
      { path: "orders", element: WhOrders() },
      { path: "connections", element: WhConnections() },
      { path: "analytics", element: WhAnalytics() },
      { path: "profile", element: WhProfile() },
    ],
  },

  {
    path: "/reseller",
    element: (
      <ProtectedRoute role="reseller">
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: RsFeed() },
      { path: "wholesalers", element: RsWholesalers() },
      { path: "orders", element: RsOrders() },
      { path: "integration", element: RsIntegration() },
      { path: "profile", element: RsProfile() },
    ],
  },

  {
    path: "/admin",
    element: (
      <ProtectedRoute role="admin">
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: AdOverview() },
      { path: "members", element: AdMembers() },
      { path: "orders", element: AdOrders() },
      { path: "market", element: AdMarket() },
      { path: "audit", element: AdAudit() },
      { path: "changelog", element: AdChangelog() },
      { path: "release-notes", element: AdReleaseNotes() },
      { path: "settings", element: AdSettings() },
    ],
  },

  { path: "/404", element: NotFoundPage() },
  { path: "*", element: <Navigate to="/404" replace /> },
]);
